import { useMemo, useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";
import { TrendingUp, Loader2, FileText, Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/shared/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import { useTranslation } from "@/shared/i18n";
import type {
  CreatorReceivableSnapshot,
  CreatorBankAccount,
  ReceivableDisplayStatus,
} from "../types";
import { getReceivableDisplayStatus } from "../types";
import { Link } from "react-router-dom";
import { MockBanner } from "./MockBanner";
import { RequestReceiptModal } from "./RequestReceiptModal";
import { EditBankAccountDialog } from "./EditBankAccountDialog";

const currency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value);

const formatMonth = (paymentMonth: string) => {
  const [y, m] = paymentMonth.split("-");
  if (!y || !m) return paymentMonth;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return format(d, "MMM yyyy", { locale: ptBR });
};

const displayStatusConfig: Record<
  ReceivableDisplayStatus,
  { labelKey: string; tooltipKey?: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  awaiting_creator_action: {
    labelKey: "receivables.statusAwaitingCreatorAction",
    tooltipKey: "receivables.statusAwaitingCreatorActionTooltip",
    variant: "secondary",
  },
  awaiting_receipt: {
    labelKey: "receivables.statusAwaitingReceipt",
    tooltipKey: "receivables.statusAwaitingReceiptTooltip",
    variant: "outline",
  },
  received: {
    labelKey: "receivables.statusReceived",
    variant: "default",
  },
};

interface ReceivablesDashboardProps {
  snapshots: CreatorReceivableSnapshot[];
  isLoading: boolean;
  periodMonths: number;
  onPeriodChange: (months: number) => void;
  bankAccounts: CreatorBankAccount[];
  onRefetch?: () => void;
}

export function ReceivablesDashboard({
  snapshots,
  isLoading,
  periodMonths,
  onPeriodChange,
  bankAccounts,
  onRefetch,
}: ReceivablesDashboardProps) {
  const { t } = useTranslation("creator-financial");
  const [requestReceiptOpen, setRequestReceiptOpen] = useState(false);
  const [initialSelectedMonth, setInitialSelectedMonth] = useState<string | null>(null);
  const [addAccountDialogOpen, setAddAccountDialogOpen] = useState(false);
  const [tablePage, setTablePage] = useState(1);

  const TABLE_PAGE_SIZE = 10;
  const sortedSnapshots = useMemo(
    () =>
      [...snapshots].sort(
        (a, b) =>
          new Date(b.paymentMonth).getTime() - new Date(a.paymentMonth).getTime()
      ),
    [snapshots]
  );
  const totalTablePages = Math.max(1, Math.ceil(sortedSnapshots.length / TABLE_PAGE_SIZE));
  const paginatedSnapshots = useMemo(
    () =>
      sortedSnapshots.slice(
        (tablePage - 1) * TABLE_PAGE_SIZE,
        tablePage * TABLE_PAGE_SIZE
      ),
    [sortedSnapshots, tablePage]
  );

  const chartData = useMemo(() => {
    return [...(snapshots ?? [])]
      .sort(
        (a, b) =>
          new Date(a.paymentMonth).getTime() - new Date(b.paymentMonth).getTime()
      )
      .map((s) => ({
        month: formatMonth(s.paymentMonth),
        shareAmount: s.shareAmount,
        grossAmount: s.grossAmount,
        netAmount: s.netAmount,
        sharePercentage: s.sharePercentage,
      }));
  }, [snapshots]);

  const totalReceived = useMemo(() => {
    return snapshots.reduce((acc, s) => acc + s.shareAmount, 0);
  }, [snapshots]);

  const lastMonthSnapshot = useMemo(() => {
    const sorted = [...snapshots].sort(
      (a, b) =>
        new Date(b.paymentMonth).getTime() - new Date(a.paymentMonth).getTime()
    );
    return sorted[0];
  }, [snapshots]);

  const monthlyAverage = useMemo(() => {
    if (snapshots.length === 0) return 0;
    return totalReceived / snapshots.length;
  }, [snapshots.length, totalReceived]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[320px]">
        <Loader2 className="h-8 w-8 animate-spin opacity-45" />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="space-y-8">
      <MockBanner />
      <div>
        <h1 className="cf-page-title">{t("receivables.title")}</h1>
        <p className="cf-page-subtitle">{t("receivables.subtitle")}</p>
        <p className="text-sm mt-1 text-[var(--cf-text-muted)]">
          {t("receivables.receiptActionsHint")}
        </p>
      </div>

      {/* Cards */}
      <div className="grid gap-5 md:grid-cols-3">
        <div className="cf-card">
          <div className="flex flex-row items-center justify-between pb-2">
            <span className="cf-label">{t("receivables.totalReceived")}</span>
            <TrendingUp className="h-4 w-4 text-[var(--cf-success)]" />
          </div>
          <div className="cf-value text-2xl font-bold mt-1">{currency(totalReceived)}</div>
          <p className="text-xs mt-1 opacity-65">{t("receivables.totalReceivedHint")}</p>
        </div>
        <div className="cf-card">
          <div className="flex flex-row items-center justify-between pb-2">
            <span className="cf-label">{t("receivables.lastMonth")}</span>
          </div>
          <div className="cf-value text-2xl font-bold mt-1">
            {lastMonthSnapshot
              ? currency(lastMonthSnapshot.shareAmount)
              : currency(0)}
          </div>
          <p className="text-xs mt-1 opacity-65">
            {lastMonthSnapshot
              ? formatMonth(lastMonthSnapshot.paymentMonth)
              : "—"}
          </p>
        </div>
        <div className="cf-card">
          <div className="flex flex-row items-center justify-between pb-2">
            <span className="cf-label">{t("receivables.monthlyAverage")}</span>
          </div>
          <div className="cf-value text-2xl font-bold mt-1">{currency(monthlyAverage)}</div>
          <p className="text-xs mt-1 opacity-65">{t("receivables.monthlyAverageHint")}</p>
        </div>
      </div>

      {/* Gráfico */}
      <div className="cf-card">
        <div className="flex flex-row items-center justify-between pb-4">
          <h2 className="text-lg font-semibold text-[var(--cf-text)]">{t("receivables.evolutionTitle")}</h2>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={periodMonths === 6 ? "default" : "outline"}
              size="sm"
              onClick={() => onPeriodChange(6)}
            >
              {t("receivables.period6Months")}
            </Button>
            <Button
              type="button"
              variant={periodMonths === 12 ? "default" : "outline"}
              size="sm"
              onClick={() => onPeriodChange(12)}
            >
              {t("receivables.period1Year")}
            </Button>
          </div>
        </div>
        <div>
          {chartData.length === 0 ? (
            <div className="h-[320px] flex items-center justify-center text-[var(--cf-text-muted)]">
              {t("receivables.empty")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cf-border)" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: "var(--cf-text-secondary)" }}
                  tickFormatter={(v) => (typeof v === "string" ? v.slice(0, 6) : v)}
                />
                <YAxis
                  tickFormatter={(v) => (v >= 1000 ? `${v / 1000}K` : String(v))}
                  tick={{ fontSize: 12, fill: "var(--cf-text-secondary)" }}
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="rounded-[var(--cf-radius-card)] border border-[var(--cf-border)] bg-[var(--cf-surface)] p-3 shadow-lg">
                        <p className="font-medium mb-2 text-[var(--cf-text)]">{d.month}</p>
                        <p className="text-sm text-[var(--cf-text-secondary)]">
                          {t("receivables.tooltipValueToReceive")}: {currency(d.shareAmount)} ({d.sharePercentage}%)
                        </p>
                      </div>
                    );
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="shareAmount"
                  stroke="var(--cf-primary)"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tabela resumo */}
      <div className="cf-card">
        <div className="flex flex-row items-center justify-between pb-4">
          <h2 className="text-lg font-semibold text-[var(--cf-text)]">{t("receivables.monthlyHistory")}</h2>
        </div>
        <div>
          {snapshots.length === 0 ? (
            <p className="text-center py-8 text-[var(--cf-text-muted)]">
              {t("receivables.empty")}
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-[var(--cf-border)] hover:bg-transparent">
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.month")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.status")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.valueToReceive")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.documents")}</TableHead>
                    <TableHead className="text-[var(--cf-text-muted)] font-medium">{t("receivables.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedSnapshots.map((s) => {
                      const displayStatus = getReceivableDisplayStatus(s);
                      const config = displayStatusConfig[displayStatus];
                      const badgeClass =
                        config.variant === "default"
                          ? "cf-badge-pill cf-badge-ativa"
                          : config.variant === "secondary"
                            ? "cf-badge-pill cf-badge-default"
                            : "cf-badge-pill cf-badge-inativa";
                      const nfeEnabled = !!s.nfeFileUrl;
                      const receiptEnabled = !!s.receiptFileUrl;
                      const nfeUrl = s.nfeFileUrl ?? undefined;
                      const receiptUrl = s.receiptFileUrl ?? undefined;
                      return (
                        <TableRow key={s.paymentMonth} className="border-[var(--cf-border)]">
                          <TableCell className="text-[var(--cf-text)]">{formatMonth(s.paymentMonth)}</TableCell>
                          <TableCell>
                            {config.tooltipKey ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={`${badgeClass} cursor-help`}>{t(config.labelKey)}</span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{t(config.tooltipKey)}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className={badgeClass}>{t(config.labelKey)}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-[var(--cf-text)]">
                            {currency(s.shareAmount)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      disabled={!nfeEnabled}
                                      onClick={() => nfeUrl && window.open(nfeUrl, "_blank")}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {nfeEnabled ? t("receivables.documentNfeTooltip") : t("receivables.documentDisabledTooltip")}
                                </TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      disabled={!receiptEnabled}
                                      onClick={() => receiptUrl && window.open(receiptUrl, "_blank")}
                                    >
                                      <Receipt className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {receiptEnabled ? t("receivables.documentReceiptTooltip") : t("receivables.documentDisabledTooltip")}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                          <TableCell>
                            {displayStatus === "awaiting_creator_action" ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        setInitialSelectedMonth(s.paymentMonth);
                                        setRequestReceiptOpen(true);
                                      }}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{t("receivables.requestReceiptTooltip")}</p>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-[var(--cf-text-muted)]">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
              <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-[var(--cf-text-muted)]">
                  {t("receivables.paginationSummary", {
                    from: snapshots.length === 0 ? 0 : (tablePage - 1) * TABLE_PAGE_SIZE + 1,
                    to: Math.min(tablePage * TABLE_PAGE_SIZE, sortedSnapshots.length),
                    total: sortedSnapshots.length,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-[var(--cf-border)] text-[var(--cf-text)]"
                    disabled={tablePage <= 1}
                    onClick={() => setTablePage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t("receivables.previous")}
                  </Button>
                  <span className="text-sm text-[var(--cf-text-muted)] px-2">
                    {t("receivables.pageOf", { current: tablePage, total: totalTablePages })}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-[var(--cf-border)] text-[var(--cf-text)]"
                    disabled={tablePage >= totalTablePages}
                    onClick={() => setTablePage((p) => Math.min(totalTablePages, p + 1))}
                  >
                    {t("receivables.next")}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-2">
                <Button variant="link" className="h-auto p-0 text-sm" asChild>
                  <Link to="/creator/solicitacoes-pagamento">
                    {t("receivables.viewPaymentRequests")}
                  </Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <RequestReceiptModal
        open={requestReceiptOpen}
        onOpenChange={(open) => {
          if (!open) setInitialSelectedMonth(null);
          setRequestReceiptOpen(open);
        }}
        snapshots={snapshots}
        bankAccounts={bankAccounts}
        initialSelectedMonth={initialSelectedMonth}
        onSuccess={onRefetch}
        onOpenAddAccount={() => {
          setRequestReceiptOpen(false);
          setInitialSelectedMonth(null);
          setAddAccountDialogOpen(true);
        }}
      />
      <EditBankAccountDialog
        open={addAccountDialogOpen}
        onOpenChange={setAddAccountDialogOpen}
        mode="create"
        account={null}
        onSuccess={() => {
          setAddAccountDialogOpen(false);
          onRefetch?.();
        }}
      />
    </div>
    </TooltipProvider>
  );
}
